/**
 * Minimal promise wrapper over a single IndexedDB object store (#805).
 *
 * Autosave outgrew `localStorage`'s ~5 MB per-origin cap once projects carry
 * embedded tracing images, so it moves to IndexedDB (quota is orders of
 * magnitude larger). We hand-roll a ~key/value shim rather than pull in a
 * library: we need exactly one store and four ops, and — critically — precise
 * control over the transaction lifecycle. `set`/`del` resolve on the
 * transaction's `complete` event (durably committed), not merely the request's
 * `success`, which the migration's copy-verify-then-delete ordering depends on.
 *
 * No automatic fallback to `localStorage` (unlike localForage): falling back
 * would silently reintroduce the very quota bug we're fixing. When IndexedDB is
 * unavailable the caller decides what to do (Autosave stays on localStorage).
 */

const DB_NAME = "leather-art";
const STORE = "autosave";
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    let req: IDBOpenDBRequest;
    try {
      // `indexedDB` can be absent, or `open` can throw synchronously in some
      // privacy modes — treat either as "unavailable".
      if (typeof indexedDB === "undefined" || indexedDB === null) {
        reject(new Error("IndexedDB unavailable"));
        return;
      }
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (e) {
      reject(e);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
    // A blocked open (another tab holds an older version open) would hang the
    // promise otherwise; surface it so the caller falls back rather than stalls.
    req.onblocked = () => reject(new Error("IndexedDB open blocked"));
  });
  // Don't cache a rejected open — a later attempt may succeed (e.g. the other
  // tab closed), and we don't want one failure to wedge the store forever.
  dbPromise.catch(() => {
    dbPromise = null;
  });
  return dbPromise;
}

/** Read a value; resolves `undefined` when the key is absent. */
export async function idbGet(key: string): Promise<string | undefined> {
  const db = await openDb();
  return new Promise<string | undefined>((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result as string | undefined);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB get failed"));
  });
}

/** Write a value, resolving only once the transaction has COMMITTED. */
export async function idbSet(key: string, value: string): Promise<void> {
  const db = await openDb();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB set failed"));
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB set aborted"));
  });
}

/** Delete a value, resolving only once the transaction has COMMITTED. */
export async function idbDelete(key: string): Promise<void> {
  const db = await openDb();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB delete failed"));
    tx.onabort = () =>
      reject(tx.error ?? new Error("IndexedDB delete aborted"));
  });
}

/** All keys currently in the store (used to prune orphaned project blobs). */
export async function idbKeys(): Promise<string[]> {
  const db = await openDb();
  return new Promise<string[]>((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAllKeys();
    req.onsuccess = () => resolve((req.result as IDBValidKey[]).map(String));
    req.onerror = () => reject(req.error ?? new Error("IndexedDB keys failed"));
  });
}

/** Probe whether IndexedDB is usable here (absent / blocked / disabled in some
 *  privacy modes all count as no). Caches nothing beyond the shared open. */
export async function idbAvailable(): Promise<boolean> {
  try {
    await openDb();
    return true;
  } catch {
    return false;
  }
}
